import React, { useState, useEffect } from "react";
import Select from "react-select";
import { DatePicker } from "rsuite";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetStaffByIdQuery,
  useGetAllProjectsForMatrixQuery,
} from "../../../store/appApi";
import { updateStaffApi } from "../../../services/staff";
import { selectUserRole } from "../../../store/authSlice";

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
];

const roleOptions = [
  { value: "Project Manager", label: "Project Manager" },
  { value: "Civil Engineer", label: "Civil Engineer" },
  { value: "Supervisor", label: "Supervisor" },
  { value: "Store manager", label: "Store manager" },
  { value: "Gardner", label: "Gardner" },
  { value: "Office boy", label: "Office boy" },
  { value: "Auditor", label: "Auditor" },
  { value: "Security Incharge", label: "Security Incharge" },
  { value: "Security Guard", label: "Security Guard" },
  { value: "Driver", label: "Driver" },
  { value: "Electrical Engineer", label: "Electrical Engineer" },
  { value: "Electrician", label: "Electrician" },
  { value: "Accountant", label: "Accountant" },
  { value: "HR Manager", label: "HR Manager" },
  { value: "Purchase Manager", label: "Purchase Manager" },
  { value: "Inventory Manager", label: "Inventory Manager" },
  { value: "Labour", label: "Labour" },
  { value: "Office Staff", label: "Office Staff" },
  { value: "General Manager", label: "General Manager" },
  { value: "Manager", label: "Manager" },
  { value: "Manager Netplus", label: "Manager Netplus" },
  { value: "Manager Real Estate", label: "Manager Real Estate" },
  { value: "Foreman", label: "Foreman" },
  { value: "Personal Assistant", label: "Personal Assistant" },
  { value: "Real Estate Sales", label: "Real Estate Sales" },
  { value: "Tele Calling", label: "Tele Calling" },
  { value: "Revenue", label: "Revenue" },
];
const MODULE_LIST = [
  { key: "finance", label: "Finance" },
  { key: "inventory", label: "Inventory" },
  { key: "workProgress", label: "Work Progress" },
  { key: "vehicles", label: "Vehicles / Machines" },
  { key: "contractors", label: "Contractors" },
  { key: "suppliers", label: "Suppliers" },
  { key: "netplusReceipts", label: "Netplus Receipts" },
  { key: "netplusCustomerStatus", label: "Netplus — Customer Status" },
  { key: "netplusComplaints", label: "Netplus — Complaints" },
  { key: "sales", label: "Sales" },
  { key: "kittyReport", label: "Kitty Report" },
  { key: "netplusBankRecon", label: "Netplus — Bank Reconciliation" },
  {
    key: "workforce",
    label: "Workforce",
    pages: [
      { key: "workforce.allStaff", label: "All Staff" },
      {
        key: "workforce.attendance.punch",
        label: "Attendance — Punch In / Out",
      },
      {
        key: "workforce.attendance.calendar",
        label: "Attendance — My Calendar",
      },
      {
        key: "workforce.attendance.roster",
        label: "Attendance — Daily Roster",
      },
      {
        key: "workforce.attendance.settings",
        label: "Attendance — Workplace Config",
      },
      { key: "workforce.leave.apply", label: "Leave — Apply" },
      { key: "workforce.leave.list", label: "Leave — My Leaves" },
      { key: "workforce.movementRegister", label: "Movement Register" },
      { key: "workforce.staffReporting", label: "Staff Reporting" },
      {
        key: "workforce.staffReporting.customers",
        label: "Staff Reporting — Customer Database",
      },
      {
        key: "workforce.staffReporting.efforts",
        label: "Staff Reporting — Team Efforts Report",
      },
      {
        key: "workforce.modemRecovery",
        label: "Staff Reporting — Modem Recovery",
      },
      { key: "workforce.advances", label: "Advances" },
      { key: "workforce.imprest", label: "Imprest (My Ledger)" },
      { key: "workforce.cpDatabase", label: "CP Database" },
    ],
  },
  { key: "contractWorkers", label: "Contract Workers" },
  { key: "labour", label: "Labour" },
  { key: "companies", label: "Companies" },
  { key: "projects", label: "Projects" },
  { key: "mdSchedule", label: "MD Schedule" },
  { key: "hotelFeedback", label: "Hotel Feedback" },
  // "bankAccounts" (Netplus Banks / Reports) is intentionally not listed here
  // — that whole menu group is super-admin only now (see Menu.jsx), so
  // granting it to a staff member would be a no-op.
];

const ALL_PAGE_KEYS = MODULE_LIST.flatMap((m) =>
  (m.pages || []).map((p) => p.key),
);

const DEFAULT_MODULES = Object.fromEntries(
  MODULE_LIST.map(({ key }) => [key, "none"]),
);

const DEFAULT_PAGE_PERMISSIONS = Object.fromEntries(
  ALL_PAGE_KEYS.map((k) => [k, "inherit"]),
);

const EditStaff = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Only super-admin can grant/change module & page permissions — everyone
  // else can still edit staff details, but the existing (unmodified)
  // permissions are preserved on submit since form.modules stays untouched.
  const canEditPermissions = useSelector(selectUserRole) === "super-admin";

  const { data: staffResponse, isLoading } = useGetStaffByIdQuery(id, {
    skip: !id,
  });
  const staffData = staffResponse?.user || null;

  const { data: projectsData } = useGetAllProjectsForMatrixQuery();
  const projectOptions = (
    projectsData?.data ??
    projectsData?.projects ??
    []
  ).map((p) => ({
    value: p._id?.toString() ?? p.id?.toString(),
    label: `${p.projectName ?? p.name}${p.city ? ` — ${p.city}` : ""}`,
  }));

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    joiningDate: null,
    adhaarCard: "",
    gender: genderOptions[0],
    role: roleOptions[0],
    password: "",
    confirmPassword: "",
    projectIds: [],
    modules: { ...DEFAULT_MODULES },
    pagePermissions: { ...DEFAULT_PAGE_PERMISSIONS },
    isManagingDirector: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!staffData) return;
    setForm({
      name: staffData.name || "",
      phone: staffData.phone || "",
      email: staffData.email || "",
      address: staffData.address || "",
      city: staffData.city || "",
      joiningDate: staffData.joiningDate
        ? new Date(staffData.joiningDate)
        : null,
      adhaarCard: staffData.adhaarCard || "",
      gender:
        genderOptions.find((o) => o.value === staffData.gender) ||
        genderOptions[0],
      role:
        roleOptions.find((o) => o.value === staffData.role) || roleOptions[0],
      password: "",
      confirmPassword: "",
      projectIds: (staffData.projectIds || []).map((p) => ({
        value: p._id?.toString() ?? p.id?.toString() ?? p.toString(),
        label: p.projectName ?? p.toString(),
      })),
      modules: { ...DEFAULT_MODULES, ...(staffData.modules || {}) },
      pagePermissions: {
        ...DEFAULT_PAGE_PERMISSIONS,
        ...(staffData.pagePermissions || {}),
      },
      isManagingDirector: Boolean(staffData.isManagingDirector),
    });
  }, [staffData]);

  const isSuperAdmin = form.role?.value === "super-admin";

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const setModulePerm = (moduleKey, level) =>
    setForm((prev) => ({
      ...prev,
      modules: { ...prev.modules, [moduleKey]: level },
    }));

  const setPagePerm = (pageKey, level) =>
    setForm((prev) => ({
      ...prev,
      pagePermissions: { ...prev.pagePermissions, [pageKey]: level },
    }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required.";
    if (!form.phone.trim()) errs.phone = "Phone is required.";
    if (form.password && form.password.length < 8)
      errs.password = "Password must be at least 8 characters.";
    if (form.password && form.password !== form.confirmPassword)
      errs.confirmPassword = "Passwords do not match.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await updateStaffApi({
        _id: staffData?.id || id,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        joiningDate: form.joiningDate,
        adhaarCard: form.adhaarCard.trim(),
        gender: form.gender?.value || "",
        role: form.role?.value || "",
        projectIds: isSuperAdmin ? [] : form.projectIds.map((opt) => opt.value),
        modules: isSuperAdmin ? DEFAULT_MODULES : form.modules,
        pagePermissions: isSuperAdmin ? {} : form.pagePermissions,
        isManagingDirector: form.isManagingDirector,
        ...(form.password ? { password: form.password } : {}),
      });
      dispatch(appApi.util.invalidateTags(["Staff"]));
      Swal.fire({
        icon: "success",
        title: "Staff updated successfully!",
        timer: 1500,
        showConfirmButton: false,
      });
      navigate("/all-staff");
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0]?.msg ||
        err?.response?.data?.message ||
        "Failed to update staff.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // Only super-admin can edit any staff profile at all.
  if (!canEditPermissions) return <Navigate to="/all-staff" replace />;

  if (isLoading) {
    return (
      <>
        <PageTitle activeMenu={"Edit Staff"} motherMenu={"Staff"} />
        <div className="row">
          <div className="col-12">
            <p>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={"Edit Staff"} motherMenu={"Staff"} />
      <div className="row">
        <div className="col-xl-12 col-xxl-12 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Basic Info</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} id="editStaffForm">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="name">
                        Full Name
                      </label>
                      <input
                        id="name"
                        type="text"
                        className="form-control"
                        value={form.name}
                        onChange={set("name")}
                        required
                      />
                      {errors.name && (
                        <small className="text-danger">{errors.name}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="phone">
                        Phone
                      </label>
                      <input
                        id="phone"
                        type="text"
                        className="form-control"
                        value={form.phone}
                        onChange={set("phone")}
                        required
                      />
                      {errors.phone && (
                        <small className="text-danger">{errors.phone}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="email">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="form-control"
                        value={form.email}
                        onChange={set("email")}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="address">
                        Address
                      </label>
                      <input
                        id="address"
                        type="text"
                        className="form-control"
                        value={form.address}
                        onChange={set("address")}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="city">
                        City
                      </label>
                      <input
                        id="city"
                        type="text"
                        className="form-control"
                        value={form.city}
                        onChange={set("city")}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Joining Date</label>
                      <div className="input-hasicon mb-xl-0 mb-3">
                        <DatePicker
                          placeholder="Joining Date"
                          className="picker-suit"
                          value={form.joiningDate}
                          onChange={(date) =>
                            setForm((prev) => ({ ...prev, joiningDate: date }))
                          }
                        />
                        <div className="icon">
                          <i className="far fa-calendar" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="adhaarCard">
                        Adhaar Card
                      </label>
                      <input
                        id="adhaarCard"
                        type="number"
                        maxLength="12"
                        className="form-control"
                        value={form.adhaarCard}
                        onChange={set("adhaarCard")}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Gender</label>
                      <Select
                        isSearchable={false}
                        value={form.gender}
                        options={genderOptions}
                        className="custom-react-select"
                        onChange={(opt) =>
                          setForm((prev) => ({ ...prev, gender: opt }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <Select
                        isSearchable={false}
                        value={form.role}
                        options={roleOptions}
                        className="custom-react-select"
                        onChange={(opt) =>
                          setForm((prev) => ({ ...prev, role: opt }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group form-check mt-4 pt-2">
                      <input
                        id="isManagingDirector"
                        type="checkbox"
                        className="form-check-input"
                        checked={form.isManagingDirector}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            isManagingDirector: e.target.checked,
                          }))
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="isManagingDirector"
                      >
                        Managing Director
                      </label>
                      <div>
                        <small className="text-muted">
                          Flags this staff member as a Managing Director so
                          their MD Schedule stays separate from other MDs.
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Project & Module assignment — hidden for super-admin */}
                  {isSuperAdmin ? (
                    <div className="col-sm-12">
                      <div className="alert alert-info d-flex align-items-center gap-2 mb-3">
                        <i className="la la-shield-alt fs-4" />
                        <span>
                          Super-Admin accounts possess unrestricted global
                          access. Project and module assignments are not
                          applicable.
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="col-sm-12">
                        <div className="form-group">
                          <label className="form-label">Assign Projects</label>
                          <Select
                            isMulti
                            isSearchable
                            value={form.projectIds}
                            options={projectOptions}
                            className="custom-react-select custom-react-select-multi"
                            placeholder="Select one or more projects..."
                            onChange={(opts) =>
                              setForm((prev) => ({
                                ...prev,
                                projectIds: opts || [],
                              }))
                            }
                          />
                          <small className="text-muted">
                            Staff can only submit data for their assigned
                            projects.
                          </small>
                        </div>
                      </div>

                      {/* Module permission matrix — super-admin only */}
                      {canEditPermissions && (
                        <div className="col-sm-12 mt-2 mb-3">
                          <label className="form-label fw-bold">
                            Module Permissions
                          </label>
                          <div className="table-responsive">
                            <table className="table table-bordered table-sm align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: "42%" }}>
                                    Module / Page
                                  </th>
                                  <th className="text-center">No Access</th>
                                  <th className="text-center">Read</th>
                                  <th className="text-center">
                                    Read &amp; Write
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {MODULE_LIST.map(({ key, label, pages }) => (
                                  <React.Fragment key={key}>
                                    <tr style={{ background: "#f0f4ff" }}>
                                      <td className="fw-bold">{label}</td>
                                      {["none", "read", "read-write"].map(
                                        (level) => (
                                          <td
                                            key={level}
                                            className="text-center"
                                          >
                                            <input
                                              type="radio"
                                              name={`module-${key}`}
                                              checked={
                                                form.modules[key] === level
                                              }
                                              onChange={() =>
                                                setModulePerm(key, level)
                                              }
                                            />
                                          </td>
                                        ),
                                      )}
                                    </tr>
                                    {pages?.map((page) => {
                                      const effectivePerm =
                                        form.pagePermissions[page.key] ===
                                        "inherit"
                                          ? form.modules[key]
                                          : form.pagePermissions[page.key];
                                      return (
                                        <tr
                                          key={page.key}
                                          style={{ background: "#fafafa" }}
                                        >
                                          <td
                                            className="small text-muted"
                                            style={{
                                              paddingLeft: 28,
                                              borderLeft: "3px solid #dee2e6",
                                            }}
                                          >
                                            ↳ {page.label}
                                            {form.pagePermissions[page.key] ===
                                              "inherit" && (
                                              <span
                                                className="ms-1 text-muted"
                                                style={{ fontSize: 10 }}
                                              >
                                                (inherited)
                                              </span>
                                            )}
                                          </td>
                                          {["none", "read", "read-write"].map(
                                            (level) => (
                                              <td
                                                key={level}
                                                className="text-center"
                                              >
                                                <input
                                                  type="radio"
                                                  name={`page-${page.key}`}
                                                  checked={
                                                    effectivePerm === level
                                                  }
                                                  onChange={() =>
                                                    setPagePerm(page.key, level)
                                                  }
                                                />
                                              </td>
                                            ),
                                          )}
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="text-muted small mt-1">
                            <strong>Messages</strong> isn't listed here —
                            it's open to every employee by default and
                            doesn't need a permission grant.
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="password">
                        New Password{" "}
                        <small className="text-muted">
                          (leave blank to keep current)
                        </small>
                      </label>
                      <div className="input-group pass-group">
                        <input
                          id="password"
                          placeholder="New Password"
                          type={showPassword ? "text" : "password"}
                          className="form-control pass-input"
                          value={form.password}
                          onChange={set("password")}
                        />
                        <span
                          className={`input-group-text pass-handle ${showPassword ? "active" : ""}`}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className="fa fa-eye-slash" />
                          <i className="fa fa-eye" />
                        </span>
                      </div>
                      {errors.password && (
                        <small className="text-danger">{errors.password}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="confirmPassword">
                        Confirm Password
                      </label>
                      <div className="input-group pass-group">
                        <input
                          id="confirmPassword"
                          placeholder="Confirm Password"
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control pass-input"
                          value={form.confirmPassword}
                          onChange={set("confirmPassword")}
                        />
                        <span
                          className={`input-group-text pass-handle ${showConfirmPassword ? "active" : ""}`}
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          <i className="fa fa-eye-slash" />
                          <i className="fa fa-eye" />
                        </span>
                      </div>
                      {errors.confirmPassword && (
                        <small className="text-danger">
                          {errors.confirmPassword}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <button
                      type="submit"
                      className="btn btn-primary me-1"
                      disabled={submitting}
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light"
                      onClick={() => navigate("/all-staff")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditStaff;
